/**
 * ④-1(2026-09-27 · FINAL_RUN §0-2·§2-4) — 디스플레이 광고 라우트 플래그.
 *
 * 정본 값: app_config (namespace='ads', key='display_routes') — { home, apt, region, board, blog, stock, calc, other }.
 * 기본값(= DB 를 못 읽을 때): 홈·현장 상세·지역 템플릿·게시판·기타 OFF, 블로그·주식·계산기 ON.
 * DB 값이라 배포 없이 1분 안에 되돌린다(서버 캐시 300초 — /api/ads/display-routes).
 *
 * OFF 라우트에는 광고 스크립트 태그(adsbygoogle — 자동광고·Funding Choices 는 이 로더가 끌고 온다)를
 * «넣지 않는다». GA·네이버 애널리틱스는 무관하다. 게이트: components/ads/AdsGate.tsx.
 */
import { isAptSiteDetailPath } from '@/lib/apt/is-site-detail';

export const AD_ROUTE_CLASSES = ['home', 'apt', 'region', 'board', 'blog', 'stock', 'calc', 'other'] as const;
export type AdRouteClass = (typeof AD_ROUTE_CLASSES)[number];
export type DisplayRoutes = Record<AdRouteClass, boolean>;

/** §0-2 JSON 과 동일. */
export const DEFAULT_DISPLAY_ROUTES: Readonly<DisplayRoutes> = Object.freeze({
  home: false,
  apt: false,
  region: false,
  board: false,
  // ads-off-1103 — Node 「애드센스 전방위로 끄기」: 설정을 못 읽어도 로더를 넣지 않는다(기본값 전부 OFF).
  blog: false,
  stock: false,
  calc: false,
  other: false,
});

/** 서버 캐시(초). 라우트 핸들러 revalidate 와 짝. */
export const DISPLAY_ROUTES_TTL_S = 300;

/** AdSense 로더 — 게이트가 ON 라우트에서만 <head> 에 넣는다. */
export const ADSENSE_CLIENT = 'ca-pub-2356113563328542';
export const ADSENSE_LOADER_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;

/** DB 값(모양 불명) → 완전한 DisplayRoutes. 불리언이 아닌 칸은 기본값. */
export function normalizeDisplayRoutes(v: unknown): DisplayRoutes {
  const out: DisplayRoutes = { ...DEFAULT_DISPLAY_ROUTES };
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out;
  for (const k of AD_ROUTE_CLASSES) {
    const x = (v as Record<string, unknown>)[k];
    if (typeof x === 'boolean') out[k] = x;
  }
  return out;
}

function under(p: string, base: string): boolean {
  return p === base || p.startsWith(`${base}/`);
}

/**
 * pathname → 라우트 클래스.
 *   home `/` · apt 현장 상세(/apt/{slug}) · region 지역 템플릿(/apt · /apt/region/** · /apt/area/**)
 *   · board /board/** · blog /blog/** · stock /stock/** · calc /calc/** · 그 외 other.
 */
export function adRouteClass(pathname: string | null | undefined): AdRouteClass {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  if (p === '/') return 'home';
  if (isAptSiteDetailPath(p)) return 'apt';
  if (p === '/apt' || under(p, '/apt/region') || under(p, '/apt/area')) return 'region';
  if (under(p, '/board')) return 'board';
  if (under(p, '/blog')) return 'blog';
  if (under(p, '/stock')) return 'stock';
  if (under(p, '/calc')) return 'calc';
  return 'other';
}
